import {
  Box,
  Chip,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faTriangleExclamation,
  faCircleQuestion
} from "@fortawesome/free-solid-svg-icons";

import ReviewTableShell from "../../../modules/review-table/ReviewTableShell";

import { bomLineFlagConfig, summarizeFile } from "./mockBomData";

const flagIconMap = {
  reported: faCircleCheck,
  assumption: faTriangleExclamation,
  insufficient: faCircleQuestion
};

const FlagChip = ({ flag }) => {
  const { palette } = useTheme();
  const config = bomLineFlagConfig[flag];

  if (!config) {
    return null;
  }

  const { colorKey, label, tooltip } = config;

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        icon={
          <FontAwesomeIcon
            icon={flagIconMap[flag]}
            style={{ color: palette[colorKey].main }}
          />
        }
        label={label}
        sx={{
          color: palette.text.primary,
          borderColor: palette[colorKey].main,
          backgroundColor: "transparent"
        }}
        variant="outlined"
      />
    </Tooltip>
  );
};

const WeightCell = ({ line, onEditWeight }) => {
  const { weightGrams, percent, weightSource } = line;

  if (weightGrams != null) {
    return <Typography variant="body2">{weightGrams} g</Typography>;
  }

  if (percent != null) {
    return <Typography variant="body2">{percent}%</Typography>;
  }

  if (weightSource === "missing") {
    return (
      <Typography variant="body2" color="error">
        Needs input
      </Typography>
    );
  }

  return (
    <TextField
      size="small"
      type="number"
      value={weightGrams ?? ""}
      onChange={(event) => onEditWeight(Number(event.target.value))}
      InputProps={{
        endAdornment: <Typography variant="caption">g</Typography>
      }}
      sx={{ maxWidth: 110 }}
    />
  );
};

const MaterialTypeCell = ({ materialType }) => {
  if (materialType) {
    return <Typography variant="body2">{materialType}</Typography>;
  }

  return (
    <Typography variant="body2" color="error">
      Unmapped material
    </Typography>
  );
};

const FlagCountChip = ({ flag, count }) => {
  const { palette } = useTheme();

  if (!count) {
    return null;
  }

  const { colorKey, label } = bomLineFlagConfig[flag];

  return (
    <Chip
      size="small"
      icon={
        <FontAwesomeIcon
          icon={flagIconMap[flag]}
          style={{ color: palette[colorKey].main }}
        />
      }
      label={`${count} ${label.toLowerCase()}`}
      variant="outlined"
      sx={{ color: palette.text.primary, borderColor: palette[colorKey].main }}
    />
  );
};

const bomColumns = (onEditLineWeight, sourceFile) => [
  {
    id: "component",
    label: "Component",
    render: ({ rawName, section }) => (
      <>
        <Typography variant="body2">{rawName}</Typography>
        <Typography variant="caption" color="textSecondary">
          {section}
        </Typography>
      </>
    )
  },
  {
    id: "composition",
    label: "Composition",
    render: ({ rawComposition }) => (
      <Typography variant="caption" color="textSecondary">
        {rawComposition || "—"}
      </Typography>
    )
  },
  {
    id: "materialType",
    label: "Material type",
    render: ({ materialType }) => (
      <MaterialTypeCell materialType={materialType} />
    )
  },
  {
    id: "weight",
    label: "Weight",
    render: (line, index) => (
      <WeightCell
        line={line}
        onEditWeight={(weight) => onEditLineWeight(sourceFile, index, weight)}
      />
    )
  },
  {
    id: "status",
    label: "Status",
    render: ({ flag }) => <FlagChip flag={flag} />
  }
];

const ProductReviewCard = ({ file, onEditLineWeight }) => {
  const { sourceFile, detectedTemplate, parseConfidence, product } = file;
  const allLines = [...(file.lines || []), ...(file.packaging || [])];

  const isLowConfidence = parseConfidence < 0.6;
  const { reportedCount, assumptionCount, insufficientCount } =
    summarizeFile(file);
  const needsAttention = isLowConfidence || insufficientCount > 0;

  return (
    <ReviewTableShell
      needsAttention={needsAttention}
      title={`${product.name} (${product.sku})`}
      subtitle={`${sourceFile} • ${detectedTemplate} • ${Math.round(
        parseConfidence * 100
      )}% confidence`}
      chips={
        <>
          <FlagCountChip flag="reported" count={reportedCount} />
          <FlagCountChip flag="assumption" count={assumptionCount} />
          <FlagCountChip flag="insufficient" count={insufficientCount} />
        </>
      }
      columns={bomColumns(onEditLineWeight, sourceFile)}
      lines={allLines}
      getLineKey={(line, index) => `${line.rawName}-${index}`}
      body={
        isLowConfidence ? (
          <Typography variant="body2" color="error">
            Low parse confidence — this product needs manual review before
            import.
          </Typography>
        ) : undefined
      }
    />
  );
};

const BomReviewTable = ({ files, onEditLineWeight }) => (
  <Box>
    {files.map((file) => (
      <ProductReviewCard
        key={file.sourceFile}
        file={file}
        onEditLineWeight={onEditLineWeight}
      />
    ))}
  </Box>
);

export default BomReviewTable;
